import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ClipboardCheck, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  MapPin,
  Plus,
  ArrowRight,
  Clock
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeClass = (risk) => {
    switch (risk) {
      case 'high_risk': return 'risk-high';
      case 'medium_risk': return 'risk-medium';
      case 'acceptable': return 'risk-acceptable';
      case 'good': return 'risk-good';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'high_risk': return 'High Risk';
      case 'medium_risk': return 'Medium Risk';
      case 'acceptable': return 'Acceptable';
      case 'good': return 'Good';
      default: return risk;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const riskChartData = stats ? [
    { name: 'High Risk', value: stats.risk_distribution.high_risk, color: '#EF4444' },
    { name: 'Medium', value: stats.risk_distribution.medium_risk, color: '#F59E0B' },
    { name: 'Acceptable', value: stats.risk_distribution.acceptable, color: '#10B981' },
    { name: 'Good', value: stats.risk_distribution.good, color: '#06B6D4' },
  ].filter(d => d.value > 0) : [];

  const actionsChartData = stats ? [
    { name: 'Open', value: stats.corrective_actions.open, fill: '#EF4444' },
    { name: 'In Progress', value: stats.corrective_actions.in_progress, fill: '#F59E0B' },
    { name: 'Completed', value: stats.corrective_actions.completed, fill: '#10B981' },
    { name: 'Verified', value: stats.corrective_actions.verified, fill: '#06B6D4' },
  ] : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Overview of your EHS audit performance</p>
        </div>
        <Button 
          onClick={() => navigate('/audits/new')} 
          className="btn-primary flex items-center gap-2"
          data-testid="new-audit-btn"
        >
          <Plus className="w-4 h-4" />
          New Audit
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover" data-testid="total-audits-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Audits</p>
                <p className="text-4xl font-bold font-['Chivo'] text-slate-900 mt-2">
                  {stats?.total_audits || 0}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {stats?.completed_audits || 0} completed
                </p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <ClipboardCheck className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="avg-score-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Average Score</p>
                <p className="text-4xl font-bold font-['Chivo'] text-slate-900 mt-2">
                  {stats?.average_score || 0}%
                </p>
                <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  Performance
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="open-actions-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Open Actions</p>
                <p className="text-4xl font-bold font-['Chivo'] text-slate-900 mt-2">
                  {stats?.corrective_actions?.open || 0}
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  Require attention
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="total-sites-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Sites</p>
                <p className="text-4xl font-bold font-['Chivo'] text-slate-900 mt-2">
                  {stats?.total_sites || 0}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Registered locations
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution */}
        <Card data-testid="risk-distribution-card">
          <CardHeader>
            <CardTitle className="text-lg font-['Chivo']">Risk Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {riskChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {riskChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 flex-wrap mt-4">
                  {riskChartData.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-sm text-slate-600">{entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No completed audits yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Corrective Actions */}
        <Card data-testid="actions-chart-card">
          <CardHeader>
            <CardTitle className="text-lg font-['Chivo']">Corrective Actions Status</CardTitle>
          </CardHeader>
          <CardContent>
            {actionsChartData.some(d => d.value > 0) ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={actionsChartData} layout="vertical">
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No corrective actions yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Audits */}
      <Card data-testid="recent-audits-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Chivo']">Recent Audits</CardTitle>
          <Button 
            variant="ghost" 
            className="text-slate-600 hover:text-slate-900"
            onClick={() => navigate('/audits')}
            data-testid="view-all-audits-btn"
          >
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_audits?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Type</th>
                    <th>Score</th>
                    <th>Risk Level</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_audits.map((audit) => (
                    <tr 
                      key={audit.id} 
                      className="cursor-pointer"
                      onClick={() => navigate(`/audits/${audit.id}`)}
                      data-testid={`audit-row-${audit.id}`}
                    >
                      <td className="font-medium">{audit.site_name}</td>
                      <td>
                        <span className="capitalize">
                          {audit.audit_type === 'central_kitchen' ? 'Central Kitchen' : 'Site'}
                        </span>
                      </td>
                      <td className="font-semibold">{audit.score_percentage}%</td>
                      <td>
                        <Badge className={`${getRiskBadgeClass(audit.risk_level)} border-0`}>
                          {getRiskLabel(audit.risk_level)}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={audit.status === 'completed' ? 'default' : 'outline'}>
                          {audit.status === 'completed' ? 'Completed' : 'In Progress'}
                        </Badge>
                      </td>
                      <td className="text-slate-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(audit.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No audits conducted yet</p>
              <Button 
                className="mt-4 btn-primary"
                onClick={() => navigate('/audits/new')}
                data-testid="start-first-audit-btn"
              >
                Start Your First Audit
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
